import React, { useState } from 'react';
import SafeSEO from '@/components/SafeSEO';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ExternalLink, CheckCircle2, Zap, Shield, Eye, BarChart3, TrendingUp, FileText, Target, PieChart, Download, Users, MessageSquare, Share2, CreditCard, BookOpen, LayoutGrid } from 'lucide-react';
import { Link } from 'react-router-dom';

const Training = () => {
  const { t } = useLanguage();

  const phases = [
    { id: 'p1', color: 'bg-blue-100 border-blue-200' },
    { id: 'p2', color: 'bg-indigo-100 border-indigo-200' },
    { id: 'p3', color: 'bg-sky-100 border-sky-200' },
    { id: 'p4', color: 'bg-teal-100 border-teal-200' },
    { id: 'p5', color: 'bg-cyan-100 border-cyan-200' },
    { id: 'p6', color: 'bg-emerald-100 border-emerald-200' },
    { id: 'p7', color: 'bg-green-100 border-green-200' },
    { id: 'p8', color: 'bg-lime-100 border-lime-200' },
    { id: 'p9', color: 'bg-amber-100 border-amber-200' },
  ];

  const advantageIcons = {
    agile: Zap,
    confidential: Shield,
    vision: Eye,
    segmentation: BarChart3,
    comparable: TrendingUp
  };

  const reportIcons = {
    executive: FileText,
    detailed: Target,
    visual: PieChart,
    findings: CheckCircle2,
    export: Download,
    foundation: Users
  };
  
  const hubIcons = {
    members: Users,
    comm: MessageSquare,
    networking: Share2,
    finance: CreditCard,
    lms: BookOpen
  };

  return (
    <>
      <SafeSEO
        title={t('training.title')}
        description={t('training.subtitle')}
        url="https://consulogic-gt.com/capacitacion"
      />

      <div className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] text-white overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">{t('training.title')}</h1>
              <p className="text-xl text-white/90 leading-relaxed">
                {t('training.subtitle')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Tabs Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <Tabs defaultValue="capacitacion" className="w-full">
              <TabsList className="grid w-full max-w-3xl mx-auto grid-cols-3 mb-12">
                <TabsTrigger value="capacitacion" className="text-base py-3">
                  {t('training.tab1')}
                </TabsTrigger>
                <TabsTrigger value="clima" className="text-base py-3">
                  {t('training.tab2')}
                </TabsTrigger>
                <TabsTrigger value="hub" className="text-base py-3">
                  {t('training.tab3')}
                </TabsTrigger>
              </TabsList>

              {/* Capacitación y Entrenamiento Tab */}
              <TabsContent value="capacitacion">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  {/* Intro Section */}
                  <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                      <div>
                        <h2 className="text-3xl font-bold text-[#0A5F7F] mb-6">
                          {t('training.capacitacion.introTitle')}
                        </h2>
                        <p className="text-gray-600 text-lg leading-relaxed mb-8">
                          {t('training.capacitacion.introDesc')}
                        </p>
                        <a href="https://lms.consulogic-gt.com" target="_blank" rel="noopener noreferrer">
                          <Button className="bg-[#0A5F7F] hover:bg-[#0D7FA3]">
                            {t('training.capacitacion.lmsBtn')}
                            <ExternalLink className="ml-2 h-4 w-4" />
                          </Button>
                        </a>
                      </div>
                      <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-2xl">
                        <img 
                          src="https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2070&auto=format&fit=crop" 
                          alt="Training and coaching session" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Program Phases */}
                  <div className="mb-12">
                    <div className="text-center mb-12">
                      <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F7F] mb-4">
                        {t('training.capacitacion.programTitle')}
                      </h2>
                      <p className="text-gray-600 max-w-3xl mx-auto">
                        {t('training.capacitacion.programSubtitle')}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {phases.map((phase, index) => (
                        <motion.div
                          key={phase.id}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                          className={`p-6 rounded-xl border ${phase.color} shadow-sm hover:shadow-lg transition-all duration-300`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="bg-white p-2 rounded-full shadow-sm">
                              <CheckCircle2 className="h-6 w-6 text-[#0A5F7F]" />
                            </div>
                            <div>
                              <h3 className="font-bold text-lg text-[#0A5F7F] mb-2">
                                {t(`training.phases.${phase.id}.title`)}
                              </h3>
                              <p className="text-gray-700 text-sm leading-relaxed">
                                {t(`training.phases.${phase.id}.desc`)}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* LMS CTA */}
                  <div className="bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] rounded-3xl p-8 md:p-16 text-center text-white relative overflow-hidden shadow-2xl">
                    <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                    <div className="relative z-10 max-w-2xl mx-auto">
                      <h2 className="text-3xl font-bold mb-6">{t('training.capacitacion.lmsTitle')}</h2>
                      <p className="text-lg text-white/90 mb-8">
                        {t('training.capacitacion.lmsDesc')}
                      </p>
                      <a href="https://lms.consulogic-gt.com" target="_blank" rel="noopener noreferrer">
                        <Button size="lg" className="bg-white text-[#0A5F7F] hover:bg-gray-100 font-bold px-8">
                          {t('training.capacitacion.lmsBtn')}
                          <ExternalLink className="ml-2 h-5 w-5" />
                        </Button>
                      </a>
                    </div>
                  </div>
                </motion.div>
              </TabsContent>

              {/* Clima Organizacional Tab - Pulso360 */}
              <TabsContent value="clima">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  {/* Pulso360 Logo */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="flex justify-center mb-12"
                  >
                    <img 
                      src="/images/pulso360.png" 
                      alt="Pulso360 logo - circular heartbeat pulse design in teal and green" 
                      className="w-48 md:w-56 h-auto rounded-2xl bg-white p-4 shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
                    />
                  </motion.div>

                  {/* Intro Section */}
                  <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-12">
                    <div className="max-w-4xl mx-auto">
                      <div className="text-center mb-8">
                        <h2 className="text-4xl font-bold text-[#0A5F7F] mb-4">Pulso360</h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-[#0A5F7F] to-[#0D7FA3] mx-auto mb-6"></div>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed mb-8">
                        {t('training.pulso360.introText')}
                      </p>
                      
                      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-8 border border-blue-100">
                        <h3 className="text-2xl font-bold text-[#0A5F7F] mb-4">
                          {t('training.pulso360.whatIsTitle')}
                        </h3>
                        <p className="text-gray-700 leading-relaxed">
                          {t('training.pulso360.whatIsDesc')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Advantages Section */}
                  <div className="mb-12">
                    <h2 className="text-3xl font-bold text-center text-[#0A5F7F] mb-10">
                      {t('training.pulso360.advantagesTitle')}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {Object.keys(advantageIcons).map((key, index) => {
                        const Icon = advantageIcons[key];
                        return (
                          <motion.div
                            key={key}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                          >
                            <div className="w-14 h-14 bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] rounded-lg flex items-center justify-center mb-4">
                              <Icon className="h-7 w-7 text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-[#0A5F7F] mb-3">
                              {t(`training.pulso360.advantages.${key}.title`)}
                            </h3>
                            <p className="text-gray-600 leading-relaxed">
                              {t(`training.pulso360.advantages.${key}.desc`)}
                            </p>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Reports Section */}
                  <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-12">
                    <h2 className="text-3xl font-bold text-center text-[#0A5F7F] mb-10">
                      {t('training.pulso360.reportsTitle')}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {Object.keys(reportIcons).map((key, index) => {
                        const Icon = reportIcons[key];
                        return (
                          <motion.div
                            key={key}
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                            className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200 hover:border-[#0A5F7F] transition-all duration-300"
                          >
                            <div className="flex items-start gap-4">
                              <div className="bg-[#0A5F7F]/10 p-3 rounded-lg">
                                <Icon className="h-6 w-6 text-[#0A5F7F]" />
                              </div>
                              <div>
                                <h3 className="font-bold text-lg text-[#0A5F7F] mb-2">
                                  {t(`training.pulso360.reports.${key}.title`)}
                                </h3>
                                <p className="text-gray-600 text-sm leading-relaxed">
                                  {t(`training.pulso360.reports.${key}.desc`)}
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Closing Statement & CTA */}
                  <div className="bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] rounded-3xl p-8 md:p-16 text-white relative overflow-hidden shadow-2xl">
                    <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                    <div className="relative z-10 max-w-3xl mx-auto text-center">
                      <p className="text-xl leading-relaxed mb-8">
                        {t('training.pulso360.closingText')}
                      </p>
                      <Link to="/contacto">
                        <Button size="lg" className="bg-white text-[#0A5F7F] hover:bg-gray-100 font-bold px-8">
                          {t('training.pulso360.contactBtn')}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              </TabsContent>

              {/* Hub CLAI HUB Tab */}
              <TabsContent value="hub">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  {/* CLAI HUB Logo */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="flex justify-center mb-12"
                  >
                    <img 
                      src="/images/claihub.png" 
                      alt="CLAI HUB Logo" 
                      className="w-48 md:w-56 h-auto rounded-2xl bg-white p-4 shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
                    />
                  </motion.div>

                  <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-12 border-t-4 border-[#0A5F7F]">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                      <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0A5F7F]/10 text-[#0A5F7F] text-sm font-bold mb-6">
                          <LayoutGrid className="h-4 w-4" />
                          <span> {t('training.hub.title')} </span>
                        </div>
                        <h2 className="text-4xl font-extrabold text-[#0A5F7F] mb-6 leading-tight">
                          {t('training.hub.subtitle')}
                        </h2>
                        <p className="text-gray-600 text-lg leading-relaxed mb-8">
                          {t('training.hub.introText')}
                        </p>
                        <Link to="/contacto">
                          <Button className="bg-[#0A5F7F] hover:bg-[#0D7FA3] h-12 px-8 text-lg rounded-xl">
                            {t('training.hub.contactBtn')}
                            <ExternalLink className="ml-2 h-5 w-5" />
                          </Button>
                        </Link>
                      </div>
                      <div className="relative group">
                        <div className="absolute -inset-4 bg-gradient-to-r from-[#0A5F7F]/20 to-[#0D7FA3]/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500"></div>
                        <div className="relative bg-gray-900 rounded-2xl p-4 shadow-2xl overflow-hidden border border-gray-800">
                          {/* Mockup UI for the Hub */}
                          <div className="grid grid-cols-2 gap-3 opacity-90">
                            <div className="bg-gray-800 rounded-lg p-4 h-32 flex flex-col justify-between border border-gray-700">
                              <Users className="h-6 w-6 text-cyan-400" />
                              <div className="h-2 w-16 bg-gray-600 rounded"></div>
                            </div>
                            <div className="bg-gray-800 rounded-lg p-4 h-32 flex flex-col justify-between border border-gray-700">
                              <MessageSquare className="h-6 w-6 text-teal-400" />
                              <div className="h-2 w-20 bg-gray-600 rounded"></div>
                            </div>
                            <div className="bg-gray-800 rounded-lg p-4 h-32 flex flex-col justify-between border border-gray-700">
                              <Share2 className="h-6 w-6 text-blue-400" />
                              <div className="h-2 w-12 bg-gray-600 rounded"></div>
                            </div>
                            <div className="bg-gray-700/50 rounded-lg p-4 h-32 flex flex-col justify-between border border-cyan-500/30">
                              <LayoutGrid className="h-6 w-6 text-white" />
                              <div className="h-2 w-14 bg-gray-400 rounded"></div>
                            </div>
                          </div>
                          <div className="mt-4 p-4 bg-gray-800/80 rounded-xl border border-gray-700">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500"></div>
                              <div className="space-y-2">
                                <div className="h-2 w-24 bg-gray-600 rounded"></div>
                                <div className="h-2 w-16 bg-gray-700 rounded"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Feature Grid */}
                  <div className="mb-16">
                    <h2 className="text-3xl font-bold text-center text-[#0A5F7F] mb-12">
                      {t('training.hub.featuresTitle')}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {Object.keys(hubIcons).map((key, index) => {
                        const Icon = hubIcons[key];
                        return (
                          <motion.div
                            key={key}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 group"
                          >
                            <div className="w-16 h-16 bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:rotate-6 transition-transform">
                              <Icon className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-[#0A5F7F] mb-4">
                              {t(`training.hub.features.${key}.title`)}
                            </h3>
                            <p className="text-gray-600 leading-relaxed">
                              {t(`training.hub.features.${key}.desc`)}
                            </p>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Closing Statement */}
                  <div className="bg-[#0A5F7F] rounded-[2.5rem] p-10 md:p-20 text-center text-white relative overflow-hidden shadow-2xl">
                    <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                    <div className="relative z-10 max-w-2xl mx-auto">
                      <p className="text-2xl font-light leading-relaxed mb-10">
                        {t('training.hub.closingText')}
                      </p>
                      <Link to="/contacto">
                        <Button size="lg" className="bg-white text-[#0A5F7F] hover:bg-gray-100 font-bold px-12 h-14 rounded-2xl text-lg shadow-xl shadow-black/20">
                          {t('training.hub.contactBtn')}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              </TabsContent>
            </Tabs>
          </div>
        </section>
      </div>
    </>
  );
};

export default Training;